import {
  type CSSProperties,
  Fragment,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import {
  type BoardColumn as BoardColumnData,
  type ProjectMemberView,
  type TaskPriority,
  type TaskView,
} from '@apm/contracts';
import { cn } from '../../../lib/utils';
import { statusTone } from '../status-color';
import { CREATE_MS, takeCreated, useCardMotion, useLeavingIds } from '../../tasks/card-motion';
import { TaskCard } from './task-card';

interface BoardColumnProps {
  readonly column: BoardColumnData;
  readonly onPage?: (offset: number) => void;
  readonly onProgress: (taskId: string, progressPct: number) => void;
  readonly onOpen: (taskId: string) => void;
  /**
   * Đổi độ ưu tiên ngay trên thẻ — APM-BOARD-k.
   *
   * Bỏ trống ⇒ nhãn ưu tiên chỉ để đọc. Quyền trên **từng thẻ** vẫn do
   * `task.canEdit` quyết ở trong `TaskCard`; chỗ này chỉ là cửa cấp dự án.
   */
  readonly onPriority?: (taskId: string, priority: TaskPriority) => void;
  /** Chỉ có khi người xem lập kế hoạch được (`project:update`) — xem `TaskCard.onDueDate`. */
  readonly onDueDate?: (taskId: string, dueDate: string) => void;
  /** Chỉ có khi người xem giao được việc (`task:assign`) — xem `TaskCard.onAssign`. */
  readonly onAssign?: (taskId: string, assigneeId: string | null) => void;
  /** Thành viên dự án cho danh sách chọn người trên thẻ — đi cùng `onAssign`. */
  readonly members?: readonly ProjectMemberView[];
  /** Cột Backlog: viền nét đứt và thẻ có hộp chọn. */
  readonly variant?: 'status' | 'backlog';
  readonly selectedIds?: ReadonlySet<string>;
  readonly onSelect?: (taskId: string, selected: boolean) => void;
  /** Thẻ trong cột này kéo được hay không — APM-CHANGE-b, xem `TaskCard`. */
  readonly draggableCards?: boolean;
  /**
   * Kéo **chỉ để đổi cột** cho người không lập kế hoạch — APM-TSP-d. Thẻ kéo
   * được khi server nói `canMove`; chuyện thả vào đâu thì `Board.onDragEnd`
   * quyết (không cùng cột, không qua Backlog, cột Done theo `canComplete`).
   */
  readonly statusDragCards?: boolean;
  /** Hiện dưới tiêu đề cột — dùng cho câu "chọn việc rồi đưa vào sprint". */
  readonly hint?: string;
  /** Menu sửa/xoá/đổi thứ tự cột. Bỏ trống = không có quyền sửa dự án. */
  readonly menu?: ReactNode;
  /**
   * Thẻ bị làm chìm ở chế độ "làm nổi" — APM-BOARD-f.
   *
   * ⚠️ Chỉ làm **mờ**, không gỡ khỏi danh sách: chế độ này tồn tại đúng để
   * thấy thẻ khớp nằm cạnh những thẻ nào. Muốn giấu hẳn thì lọc từ chỗ gọi
   * (`Board`), và chỗ đó biết phải giữ `dropIndex` tính trên danh sách đầy đủ.
   */
  readonly dimmedIds?: ReadonlySet<string>;
  /**
   * Khung trống cho thẻ đang được kéo tới — APM-BOARD-perf3.
   *
   * `index` là chỗ nó chen vào giữa danh sách **đầy đủ** của cột. `height` là
   * chiều cao đo được của thẻ đang nhấc; `null` ⇒ dùng chiều cao đỡ.
   *
   * Bỏ trống ⇒ cú kéo hiện tại không nhắm vào cột này, hoặc không có cú kéo nào.
   */
  readonly dropIndicator?: { readonly index: number; readonly height: number | null };
  /**
   * Thẻ vừa được thả vào cột khác — APM-BOARD-land.
   *
   * Mỗi thẻ chạy một lần hiệu ứng "đáp xuống" (`.board-land`) rồi thôi, nên
   * `Board` gỡ id ra sau khi hiệu ứng chạy xong. Bỏ trống ⇒ không thẻ nào vừa
   * đáp, tức mọi lượt vẽ lại bình thường.
   *
   * ⚠️ Một **tập**, không phải một id. Hiệu ứng dài giây rưỡi, mà sắp board là
   * kéo liên tiếp: thẻ thứ hai thả xuống lúc thẻ thứ nhất còn đang nở. Giữ một
   * id thì thẻ trước bị cắt giữa chừng và nhảy phắt sang trạng thái cuối — sai
   * ở đúng thao tác thường nhất.
   *
   * ⚠️ Và là một tập **id**, không phải cờ trên từng thẻ: mọi thẻ khác trong
   * cột phải đứng yên — chúng không vừa đi đâu cả.
   */
  readonly landedIds?: ReadonlySet<string>;
}

export function BoardColumn({
  column,
  onPage,
  onProgress,
  onOpen,
  onPriority,
  onDueDate,
  onAssign,
  members,
  variant = 'status',
  selectedIds,
  draggableCards = true,
  statusDragCards = false,
  onSelect,
  hint,
  menu,
  dimmedIds,
  dropIndicator,
  landedIds,
}: BoardColumnProps): ReactNode {
  const { t } = useTranslation();

  /*
    Thẻ đang biến mất (APM-BOARD-fx) mà dữ liệu mới đã bỏ đi: vẽ lại nó ở **đúng
    chỗ cũ** cho tới khi hiệu ứng xong — xem `useLeavingIds`. Chỗ cũ lấy từ
    lượt vẽ trước (`previous`), vì dữ liệu mới không còn nói gì về thẻ ấy.
    Thẻ "bóng" không kéo được và không nhận khung trống.
  */
  const leavingIds = useLeavingIds();
  const previous = useRef<readonly TaskView[]>([]);
  const rendered = useMemo(() => {
    const out: { task: TaskView; at: number; ghost: boolean }[] = column.tasks.map((task, at) => ({
      task,
      at,
      ghost: false,
    }));
    if (leavingIds.size === 0) return out;
    const present = new Set(column.tasks.map((task) => task.id));
    previous.current.forEach((task, index) => {
      if (!leavingIds.has(task.id) || present.has(task.id)) return;
      out.splice(Math.min(index, out.length), 0, { task, at: -1, ghost: true });
    });
    return out;
  }, [column.tasks, leavingIds]);
  useLayoutEffect(() => {
    previous.current = rendered.map((entry) => entry.task);
  }, [rendered]);

  // Đăng ký cả **cột** làm vùng thả, không chỉ từng thẻ: cột rỗng không có thẻ
  // nào để đè lên, và đó lại đúng là cột người ta hay kéo tới nhất.
  const { setNodeRef, isOver } = useDroppable({
    id: column.status.id,
    // `kind` để phép dò va chạm phân biệt được cột với thẻ: cả hai id đều là
    // UUID, nên không có cách nào đọc ra từ chính chuỗi id (xem `boardCollision`).
    data: { kind: 'column' },
  });

  /*
    Tiêu đề cột **tô kín màu trạng thái** — APM-BOARD-i.

    Cùng một màu với ô trạng thái ở bảng Danh sách, nên hai màn hình kể cùng
    một câu chuyện: người dùng học cặp "cột ↔ màu" một lần rồi đọc được cả hai.
    Một dải màu ở đầu cột cũng là thứ bắt được ở tầm nhìn ngoại vi trong lúc
    kéo thẻ — đúng lúc mắt đang bận nhìn con trỏ.

    Backlog **không** tô: nó là hàng đợi, không phải một chặng của quy trình, và
    cho nó một màu ngang hàng với các cột thật là nói sai điều đó. Nó giữ nét
    đứt như cũ.
  */
  const tone = variant === 'backlog' ? null : statusTone(column.status);

  return (
    <section
      ref={setNodeRef}
      aria-label={column.status.name}
      className={cn(
        // `overflow-hidden` để dải màu của header bị bo theo góc cột; không có
        // nó thì màu tràn ra bốn góc vuông nhô khỏi khung.
        'flex w-72 min-h-0 shrink-0 flex-col overflow-hidden rounded-DEFAULT bg-muted/40 transition-colors',
        isOver && 'bg-muted',
        variant === 'backlog' && 'border border-dashed border-border bg-background',
      )}
    >
      <header
        className={cn(
          'flex items-center gap-1 px-2 py-1.5',
          // Không có màu thì vẫn cần một đường phân cách, nếu không tiêu đề
          // trôi lẫn vào thẻ đầu tiên.
          tone === null && 'border-b border-border',
        )}
        style={tone ? { backgroundColor: tone.background, color: tone.foreground } : undefined}
      >
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium">{column.status.name}</h3>
        {menu}
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs tabular-nums',
            // Màu không phải kênh thông tin duy nhất — con số bên cạnh nói rõ
            // "3/2", và `title` giải thích thành câu cho trình đọc màn hình.
            column.overWipLimit
              ? 'bg-rag-red text-white'
              : tone
                ? // Trên nền đã tô màu thì huy hiệu đếm mượn chính màu chữ của
                  // header làm nền mờ — một ô trắng ở đó đọc như một nhãn khác.
                  'bg-black/10'
                : 'bg-background text-muted-foreground',
          )}
          title={
            column.status.wipLimit === null
              ? undefined
              : t('board.wipTitle', { count: column.count, limit: column.status.wipLimit })
          }
        >
          {column.status.wipLimit === null
            ? column.count
            : `${column.count}/${column.status.wipLimit}`}
        </span>
      </header>

      {hint ? <p className="px-3 pt-2 text-[11px] text-muted-foreground">{hint}</p> : null}

      {/*
        Không còn `SortableContext` — APM-BOARD-perf4, xem docblock của `TaskCard`.
        Nó đọc public context của dnd-kit, thứ đổi ở **mỗi lần con trỏ nhích**,
        nên mỗi `useSortable` bên dưới cũng vẽ lại đúng tần suất ấy. Board này
        tự dựng khung trống và tự tính chỗ rơi nên không cần gì từ nó.
      */}
      <>
        {/* Danh sách thẻ cuộn **bên trong** cột: cột cao hết khung nhìn, còn
            phần thừa ra thì cuộn ở đây chứ không đẩy cả trang dài thêm. */}
        <div className="flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto p-2">
          {rendered.map(({ task, at, ghost }) => (
            <Fragment key={task.id}>
              {!ghost && dropIndicator?.index === at ? (
                <DropSlot height={dropIndicator.height} />
              ) : null}
              <ColumnCard
                task={task}
                /* PM kéo để lập kế hoạch (APM-CHANGE-b); người khác kéo để đổi
                   cột khi rule dự án cho phép (APM-TSP-d); người tạo việc kéo
                   được nó giữa backlog và sprint (APM-SPR-own). Cửa thật ở server. */
                draggable={
                  !ghost &&
                  ((draggableCards && (task.canEdit ?? true)) ||
                    (statusDragCards && task.canMove === true) ||
                    task.canPlanSprint === true)
                }
                dimmed={dimmedIds?.has(task.id) ?? false}
                landing={landedIds?.has(task.id) ?? false}
                selectable={onSelect !== undefined}
                selected={selectedIds?.has(task.id) ?? false}
                onProgress={onProgress}
                onOpen={onOpen}
                onPriority={onPriority}
                onDueDate={onDueDate}
                onAssign={onAssign}
                members={members}
                onSelect={onSelect}
              />
            </Fragment>
          ))}
          {/* Thả xuống **cuối** cột, và cột rỗng — cả hai đều là `index` bằng
              số thẻ hiện có, nên không có nhánh riêng nào cho cột rỗng. */}
          {dropIndicator !== undefined && dropIndicator.index >= column.tasks.length ? (
            <DropSlot height={dropIndicator.height} />
          ) : null}
          {column.tasks.length === 0 ? (
            <p className="px-1 py-6 text-center text-xs text-muted-foreground">
              {t('board.emptyColumn')}
            </p>
          ) : null}
        </div>
      </>
      {column.page && onPage && column.page.total > column.page.limit && (
        <div className="flex items-center justify-between gap-1 border-t p-2 text-xs">
          <button
            type="button"
            className="disabled:opacity-40"
            disabled={column.page.offset === 0}
            onClick={() => onPage(Math.max(0, column.page!.offset - column.page!.limit))}
          >
            {t('common.previous')}
          </button>
          <span>
            {column.page.offset + 1}–
            {Math.min(column.page.offset + column.page.limit, column.page.total)} /{' '}
            {column.page.total}
          </span>
          <button
            type="button"
            className="disabled:opacity-40"
            disabled={column.page.offset + column.page.limit >= column.page.total}
            onClick={() => onPage(column.page!.offset + column.page!.limit)}
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </section>
  );
}

export const DROP_SLOT_LABEL = 'board-drop-slot';

/**
 * Khung trống đúng bằng thẻ đang nhấc. Không chữ, không viền đặc — chỉ một chỗ.
 *
 * `aria-hidden` vì nó không mang thông tin nào cho người đọc màn hình: cú kéo
 * bằng bàn phím đã có thông báo riêng của dnd-kit. `data-slot` chỉ để test bám
 * vào — repo không dùng `data-testid`, và bám vào lớp CSS thì mỗi lần chỉnh
 * khoảng cách là một test đỏ.
 */
function DropSlot({ height }: { readonly height: number | null }): ReactNode {
  return (
    <div
      aria-hidden
      data-slot={DROP_SLOT_LABEL}
      className="shrink-0 rounded-DEFAULT border border-dashed border-primary/60 bg-primary/5"
      // `min-h` chỉ để đỡ khi chưa đo được: một khung cao 0px làm cột giật một
      // nhịp đúng lúc bắt đầu kéo.
      style={height !== null ? { height } : { minHeight: '4rem' }}
    />
  );
}

interface ColumnCardProps {
  readonly task: TaskView;
  readonly draggable: boolean;
  readonly dimmed: boolean;
  /** Thẻ này vừa được thả vào cột — chạy một lần hiệu ứng đáp xuống. */
  readonly landing: boolean;
  readonly selectable: boolean;
  readonly selected: boolean;
  readonly onProgress: (taskId: string, progressPct: number) => void;
  readonly onOpen: (taskId: string) => void;
  readonly onPriority: ((taskId: string, priority: TaskPriority) => void) | undefined;
  readonly onDueDate: ((taskId: string, dueDate: string) => void) | undefined;
  readonly onAssign: ((taskId: string, assigneeId: string | null) => void) | undefined;
  readonly members: readonly ProjectMemberView[] | undefined;
  readonly onSelect: ((taskId: string, selected: boolean) => void) | undefined;
}

/**
 * Một hàng thẻ, có `memo` — APM-BOARD-perf.
 *
 * ## Vì sao cần lớp này thay vì gọi thẳng `TaskCard`
 *
 * `TaskCard` nhận callback **không kèm id** (`onProgress(progressPct)`), một hợp
 * đồng gọn và đúng cho chính nó. Cái giá là chỗ gọi phải khép id vào, và
 * `onProgress={(pct) => onProgress(task.id, pct)}` viết thẳng trong `map` sẽ
 * dựng một hàm mới ở **mỗi lần vẽ** — nên `memo` trên thẻ không chặn được gì.
 *
 * Đo trước khi sửa: một lần `Board` vẽ lại kéo theo **60/60** thẻ vẽ lại, và
 * `onDragOver` bắn theo từng pixel. Đó là cái giật khi kéo thả.
 *
 * Lớp này khép id **một lần cho mỗi thẻ** (`useCallback` phụ thuộc `task.id` và
 * hàm của cha), rồi tự chặn ở `memo`. Hợp đồng của `TaskCard` không đổi, nên
 * test của nó cũng không đổi.
 *
 * ⚠️ Hàm mà `Board` truyền xuống phải ổn định (`useCallback`), nếu không lớp
 * này vô tác dụng — `board-render.perf.spec.tsx` canh đúng điều đó.
 */
const ColumnCard = memo(function ColumnCard({
  task,
  draggable,
  dimmed,
  landing,
  selectable,
  selected,
  onProgress,
  onOpen,
  onPriority,
  onDueDate,
  onAssign,
  members,
  onSelect,
}: ColumnCardProps): ReactNode {
  const handleProgress = useCallback(
    (progressPct: number) => onProgress(task.id, progressPct),
    [onProgress, task.id],
  );
  const handleOpen = useCallback(() => onOpen(task.id), [onOpen, task.id]);
  const handlePriority = useCallback(
    (priority: TaskPriority) => onPriority?.(task.id, priority),
    [onPriority, task.id],
  );
  const handleDueDate = useCallback(
    (dueDate: string) => onDueDate?.(task.id, dueDate),
    [onDueDate, task.id],
  );
  const handleAssign = useCallback(
    (assigneeId: string | null) => onAssign?.(task.id, assigneeId),
    [onAssign, task.id],
  );
  const handleSelect = useCallback(
    (value: boolean) => onSelect?.(task.id, value),
    [onSelect, task.id],
  );

  /*
    Hiệu ứng vào / ra do nơi khác phát — APM-BOARD-fx (`card-motion.ts`).

    - `created`: việc vừa tạo. Thẻ nhận hiệu ứng **một lần** (`takeCreated`),
      cuộn tới chỗ mình rồi đáp xuống như thẻ vừa kéo sang cột mới.
    - `leaving`: việc vừa xoá. Đo chiều cao thật trước khi co, để phần co bắt
      đầu ngay từ khung hình đầu (xem `.board-leave`).
  */
  const motion = useCardMotion(task.id);
  const wrapper = useRef<HTMLDivElement>(null);
  const [justCreated, setJustCreated] = useState(false);
  const [leaveHeight, setLeaveHeight] = useState<number | null>(null);

  useEffect(() => {
    if (motion !== 'created' || !takeCreated(task.id)) return;
    setJustCreated(true);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    wrapper.current?.scrollIntoView?.({
      behavior: reduced ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [motion, task.id]);

  // Hẹn giờ gỡ tách riêng: `takeCreated` làm `motion` về `null`, và nếu hẹn giờ
  // nằm trong effect trên thì lượt chạy lại ấy dọn luôn nó — thẻ giữ lớp mãi.
  useEffect(() => {
    if (!justCreated) return;
    const timer = setTimeout(() => setJustCreated(false), CREATE_MS);
    return () => clearTimeout(timer);
  }, [justCreated]);

  useLayoutEffect(() => {
    if (motion === 'leaving') setLeaveHeight(wrapper.current?.offsetHeight ?? null);
  }, [motion]);

  const leavingNow = motion === 'leaving';

  return (
    <div
      ref={wrapper}
      data-board-card={task.id}
      className={cn(
        'transition-opacity',
        dimmed && 'opacity-30 hover:opacity-60',
        (landing || justCreated) && !leavingNow && 'board-land',
        leavingNow && 'board-leave',
      )}
      style={
        leavingNow && leaveHeight !== null
          ? ({ '--board-leave-h': `${leaveHeight}px` } as CSSProperties)
          : undefined
      }
    >
      <TaskCard
        task={task}
        draggable={draggable}
        onProgress={handleProgress}
        onOpen={handleOpen}
        {...(onPriority ? { onPriority: handlePriority } : {})}
        {...(onDueDate && (task.canSchedule ?? true) ? { onDueDate: handleDueDate } : {})}
        {...(onAssign ? { onAssign: handleAssign, ...(members ? { members } : {}) } : {})}
        {...(selectable ? { selected, onSelect: handleSelect } : {})}
      />
    </div>
  );
});
